import React, { useCallback } from 'react';
import { View, StyleSheet, Alert } from 'react-native';
import { useSelector } from 'react-redux';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import { IntelligentNotificationCenter } from '../components/ai';
import { selectCurrentUser } from '../store/slices/authSlice';
import { useNotificationEngagement } from '../hooks/useNotificationEngagement';
import type { SmartNotification } from '@coffeeplexity/shared';
import { theme } from '../theme';
import { logger } from '../utils/logger';

type NavigationProp = StackNavigationProp<any>;

export const NotificationCenterScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const user = useSelector(selectCurrentUser);

  const {
    recordEngagement,
    submitFeedback,
    getEngagementMetrics,
    getTimingRecommendations,
  } = useNotificationEngagement(user?.id || '');

  // Handle notification press
  const handleNotificationPress = useCallback(
    async (notification: SmartNotification) => {
      try {
        // Record engagement
        await recordEngagement(notification, 'clicked');

        // Navigate based on notification type
        switch (notification.type) {
          case 'activity_reminder':
          case 'streak_reminder':
            navigation.navigate('journey');
            break;
          case 'new_recommendation':
          case 'content_suggestion':
            if (notification.metadata['contentId']) {
              navigation.navigate('recipes', {
                screen: 'recipeDetail',
                params: { recipeId: notification.metadata['contentId'] },
              });
            } else {
              navigation.navigate('recipes');
            }
            break;
          case 'milestone_achievement':
          case 'skill_mastery':
          case 'celebration':
            navigation.navigate('achievements');
            break;
          case 'journey_adaptation':
            navigation.navigate('journeyPreview');
            break;
          case 'social_activity':
            navigation.navigate('social');
            break;
          case 're_engagement':
            navigation.navigate('home');
            break;
          default:
            navigation.navigate('home');
        }
      } catch (error) {
        logger.error('Failed to handle notification press:', error);
        Alert.alert(
          'Error',
          'Failed to process notification. Please try again.'
        );
      }
    },
    [navigation, recordEngagement]
  );

  // Handle settings press
  const handleSettingsPress = useCallback(() => {
    const metrics = getEngagementMetrics();
    const recommendations = getTimingRecommendations();

    Alert.alert(
      'Notification Insights',
      `Engagement Rate: ${Math.round(metrics.engagementRate * 100)}%\n\n` +
        `Recommendations:\n${recommendations.recommendations.join('\n')}\n\n` +
        `Confidence: ${Math.round(recommendations.confidence * 100)}%`,
      [
        { text: 'OK', style: 'default' },
        {
          text: 'Optimize Now',
          style: 'default',
          onPress: () => {
            // This would trigger optimization in a real implementation
            Alert.alert(
              'Optimization',
              'Notification timing has been optimized!'
            );
          },
        },
      ]
    );
  }, [getEngagementMetrics, getTimingRecommendations]);

  if (!user) {
    return (
      <View
        style={[styles.container, { backgroundColor: theme.colors.background }]}
      >
        <View style={styles.errorContainer}>
          <IntelligentNotificationCenter
            userId=''
            onNotificationPress={handleNotificationPress}
            onSettingsPress={handleSettingsPress}
          />
        </View>
      </View>
    );
  }

  return (
    <View
      style={[styles.container, { backgroundColor: theme.colors.background }]}
    >
      <IntelligentNotificationCenter
        userId={user.id}
        onNotificationPress={handleNotificationPress}
        onSettingsPress={handleSettingsPress}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 20,
  },
});
